import { motion, useMotionValue, useTransform } from "framer-motion";
import { Play, Sparkles, Clock } from "lucide-react";
import FloatingBubbles from "@/components/FloatingBubbles";
import { scrollToHash } from "@/components/Navbar";

const ART = "/hero-art.png";

const PALETTE = ["#FF75B5", "#FFD034", "#70C1FF", "#4CD964", "#B57EDC", "#FF8C42", "#FF75B5"];

function KineticWord({ word, baseDelay, sizeClass }) {
  return (
    <span className="flex" aria-label={word}>
      {word.split("").map((ch, i) => (
        <span key={i} className="overflow-hidden pb-1 pt-1">
          <motion.span
            className={`inline-block font-display font-bold leading-none ${sizeClass}`}
            style={{ color: PALETTE[i % PALETTE.length], textShadow: "0 4px 0 rgba(30,41,59,0.12)" }}
            initial={{ y: "115%", rotate: 8 }}
            animate={{ y: 0, rotate: 0 }}
            transition={{
              delay: baseDelay + i * 0.055,
              type: "spring",
              stiffness: 160,
              damping: 13,
            }}
          >
            {ch}
          </motion.span>
        </span>
      ))}
    </span>
  );
}

export default function Hero() {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useTransform(my, [-0.5, 0.5], [7, -7]);
  const rotateY = useTransform(mx, [-0.5, 0.5], [-7, 7]);
  const artX = useTransform(mx, [-0.5, 0.5], [-14, 14]);
  const artY = useTransform(my, [-0.5, 0.5], [-10, 10]);

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section
      id="top"
      data-testid="hero-section"
      onMouseMove={onMove}
      className="relative overflow-hidden pb-16 pt-32 sm:pt-40 lg:pb-24"
      style={{ background: "linear-gradient(180deg, #E0F2FE 0%, #F0FDF4 55%, #FFFBEB 100%)" }}
    >
      <FloatingBubbles count={10} />

      <div className="relative z-10 mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:gap-8 lg:px-8">
        <div>
          <motion.div
            data-testid="hero-eyebrow"
            className="mb-6 inline-flex items-center gap-2 rounded-full border-2 border-bubz-sun bg-white/80 px-4 py-1.5 text-xs font-extrabold uppercase tracking-widest text-bubz-ink shadow-sm"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
          >
            <Sparkles size={14} className="text-bubz-tang" /> A J.J. STONE Original Series
          </motion.div>

          <h1 data-testid="hero-title" className="max-w-xl">
            <motion.span
              className="block"
              initial={{ opacity: 0, scale: 0.55, y: 50, rotate: -4 }}
              animate={{ opacity: 1, scale: 1, y: 0, rotate: 0 }}
              transition={{ delay: 0.3, type: "spring", stiffness: 120, damping: 12 }}
            >
              <motion.img
                src="/logo.png"
                alt="BUBZ & BUBBLES"
                className="w-full max-w-lg drop-shadow-[0_20px_35px_rgba(255,117,181,0.35)]"
                animate={{ y: [0, -10, 0], rotate: [-1.2, 1.2, -1.2] }}
                transition={{ duration: 5.5, repeat: Infinity, ease: "easeInOut" }}
              />
            </motion.span>
          </h1>

          <motion.p
            data-testid="hero-tagline"
            className="mt-6 max-w-md text-base font-semibold leading-relaxed text-bubz-ink/70 sm:text-lg"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.6 }}
          >
            Every week, a curious little girl named Bubz and her best friend — a real, live Bubble —
            pop into a brand-new 2-minute adventure. Free on YouTube!
          </motion.p>

          <motion.div
            className="mt-8 flex flex-wrap items-center gap-4"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2, duration: 0.6 }}
          >
            <motion.a
              data-testid="hero-watch-button"
              href="https://www.youtube.com/@BUBZPLUSSTREAMINGSERVICE"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2.5 rounded-full bg-bubz-pink px-7 py-4 font-display text-lg font-semibold text-white shadow-[0_10px_30px_rgba(255,117,181,0.55)]"
              whileHover={{ scale: 1.06, rotate: -1 }}
              whileTap={{ scale: 0.94 }}
            >
              <Play size={20} fill="currentColor" /> Watch the Show
            </motion.a>
            <motion.button
              data-testid="hero-episodes-button"
              onClick={() => scrollToHash("#episodes")}
              className="flex items-center gap-2.5 rounded-full border-[3px] border-bubz-ink bg-white px-7 py-[15px] font-display text-lg font-semibold text-bubz-ink"
              whileHover={{ scale: 1.06, rotate: 1 }}
              whileTap={{ scale: 0.94 }}
            >
              See the Episodes
            </motion.button>
          </motion.div>

          <motion.div
            data-testid="hero-weekly-badge"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-bubz-grass/15 px-4 py-2 text-sm font-extrabold text-bubz-ink"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.4 }}
          >
            <Clock size={15} className="text-bubz-grass" /> New 2-minute episode every week
          </motion.div>
        </div>

        <motion.div
          data-testid="hero-artwork"
          className="relative mx-auto w-full max-w-lg"
          style={{ x: artX, y: artY }}
          initial={{ opacity: 0, scale: 0.85, rotate: 6 }}
          animate={{ opacity: 1, scale: 1, rotate: 2 }}
          transition={{ delay: 0.4, type: "spring", stiffness: 70, damping: 14 }}
        >
          <div className="absolute -inset-10 -z-10 rounded-full bg-[radial-gradient(circle,rgba(255,208,52,0.5)_0%,rgba(255,208,52,0)_65%)]" />
          <motion.div
            style={{ rotateX, rotateY, transformPerspective: 1000 }}
            className="overflow-hidden rounded-[3rem] border-8 border-white shadow-[0_30px_80px_rgba(112,193,255,0.45)]"
          >
            <img
              src={ART}
              alt="A sunny BUBZ & BUBBLES episode scene: the tree-lined road to the zoo gates, topped with a friendly lion and watched by two elephants"
              className="h-auto w-full object-cover"
            />
          </motion.div>
          <motion.div
            className="animate-wiggle absolute -right-4 -top-5 rounded-full bg-bubz-sun px-5 py-2.5 font-display text-sm font-semibold text-bubz-ink shadow-lg sm:-right-6"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 1.3, type: "spring", stiffness: 200, damping: 9 }}
          >
            Season 1 on YouTube!
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
